import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import moment from "moment-timezone";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import axios from "axios";
import L from "leaflet";
import "./Reports.css";
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";
import CommonTable from "../../../../common/CommonTable";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.6px", mb: 2 }}>
    {text}
  </Typography>
);

export default function Reports() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${backendUrl}EarthQuake/eq_reports_all_raw/`);
      const uniqueEvents = response.data.filter(
        (v, i, a) =>
          a.findIndex(
            (t) =>
              t.latitude === v.latitude &&
              t.longitude === v.longitude &&
              t.date_time === v.date_time
          ) === i
      );
      setEvents(uniqueEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const tableRows = useMemo(() => events.map((event) => ({
    ...event,
    dateTimeText: moment.utc(event.date_time).tz("Asia/Dili").format("DD MMM YYYY hh:mm:ss A"),
    feltItText: capitalizeFirstLetter(event.felt_it),
  })), [events]);

  const columns = useMemo(() => [
    { key: "dateTimeText", label: "Date & Time", sortable: true },
    { key: "feltItText", label: "Felt it" },
    { key: "municipality_name", label: "Municipality", sortable: true },
    { key: "sub_municipality_name", label: "Sub Municipality", sortable: true },
    { key: "suco_name", label: "Suco", sortable: true },
    { key: "location", label: "Location" },
    { key: "situation", label: "Situation" },
    { key: "building_structure", label: "Building Structure" },
    { key: "ground_shaking", label: "Ground Shaking" },
    { key: "latitude", label: "Latitude", align: "right" },
    { key: "longitude", label: "Longitude", align: "right" },
    { key: "message", label: "Message" },
  ], []);

  const createPulsingIcon = () =>
    L.divIcon({
      html: `
        <div class="pulse-marker-eq-reports">
          <div class="pulse-ring-eq-reports"></div>
          <div class="pulse-dot-eq-reports"></div>
        </div>
      `,
      className: "",
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15],
    });

  return (
    <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>
              Earthquake Felt Reports
            </Typography>
            {!loading && (
              <Chip
                label={`${events.length} ${events.length === 1 ? "Report" : "Reports"}`}
                size="small"
                sx={{ fontSize: "12px", fontWeight: 600, background: "#2563eb", color: "#fff", height: "22px" }}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
            View and explore earthquake felt reports on map and table
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <Typography sx={{ fontSize: "15px", color: "#64748b" }}>Loading earthquake reports...</Typography>
          </Box>
        ) : (
          <>
            {/* Map Section */}
            {events.length > 0 && (
              <>
                <Box>
                  <Box sx={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", position: "relative" }}>
                    <MapContainer
                      center={[-8.874217, 125.727539]}
                      zoom={6}
                      style={{ height: "450px", width: "100%" }}
                      attributionControl={false}
                    >
                      <MapHomeControl />
                      <MapInfoControl text="Locations of submitted earthquake reports. Click a marker to view report details." />
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <MarkerClusterGroup
                        chunkedLoading
                        iconCreateFunction={(cluster) => {
                          const count = cluster.getChildCount();
                          return L.divIcon({
                            html: `<div class="custom-cluster-eq-reports">${count}</div>`,
                            className: "custom-cluster-wrapper",
                            iconSize: L.point(40, 40, true),
                          });
                        }}
                      >
                        {events.map((event, idx) => (
                          <Marker key={idx} position={[event.latitude, event.longitude]} icon={createPulsingIcon()}>
                            <Popup className="popup-eq-reports">
                              <strong><i><u>{event.location}</u></i></strong>
                              <br /><strong>Felt it:</strong> {capitalizeFirstLetter(event.felt_it)}
                              <br /><strong>Municipality:</strong> {event.municipality_name}
                              <br /><strong>Sub-Municipality:</strong> {event.sub_municipality_name}
                              <br /><strong>Suco:</strong> {event.suco_name}
                              <br /><strong>Situation:</strong> {event.situation}
                              <br /><strong>Building Structure:</strong> {event.building_structure}
                              <br /><strong>Ground Shaking:</strong> {event.ground_shaking}
                              <br /><strong>Date & Time:</strong> {moment.utc(event.date_time).tz("Asia/Dili").format("DD MMM YYYY hh:mm:ss A")}
                              <br /><strong>Message:</strong> {event.message}
                            </Popup>
                          </Marker>
                        ))}
                      </MarkerClusterGroup>
                    </MapContainer>

                    {/* Legend */}
                    <Box sx={{
                      position: "absolute", bottom: 10, left: 10, zIndex: 1000,
                      background: "white", borderRadius: "8px", px: 1.5, py: 1,
                      border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                      fontSize: "13px", display: "flex", flexDirection: "column", gap: 0.5,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", background: "#2563eb", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "12px", color: "#374151" }}>Reported Events</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(37,99,235,0.6)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontSize: "9px", color: "white", fontWeight: 700 }}>N</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#374151" }}>Cluster of Events</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "#e2e8f0" }} />
              </>
            )}

            {/* Table Section */}
            <Box>
              <SectionLabel text="Reports List" />

              {events.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, px: 3, background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <Typography sx={{ fontSize: "15px", color: "#64748b", mb: 1 }}>No earthquake reports available</Typography>
                  <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>Reports will appear here once data is submitted</Typography>
                </Box>
              ) : (
                <CommonTable
                  tableData={tableRows}
                  columns={columns}
                  searchableKeys={["location", "municipality_name", "sub_municipality_name", "suco_name", "message"]}
                  tableName="Earthquake_Felt_Reports"
                  emptyMessage="No earthquake reports found."
                  defaultSort={{ key: "dateTimeText", direction: "desc" }}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}
